'use client'

import {
    Button as BaseComponent,
    type ButtonProps as BaseProps,
    useStyleConfig,
} from '@chakra-ui/react'
import { merge } from 'lodash'
import React from 'react'

type Omitted =
    | 'rightIcon'
    | 'leftIcon'
    | 'iconSpacing'
    | 'spinner'
    | 'loadingText'
    | 'variant'
    | 'size'
    | 'colorScheme'

export type ButtonProps = Omit<BaseProps, Omitted> & {
    /**
     * @default "sm"
     */
    size?: 'xs' | 'sm' | 'md' | 'lg'
    /**
     * @default 'solid'
     */
    variant?:
        | 'outline'
        | 'solid'
        | 'underlined'
        | 'ghost'
        | 'subtle-ghost'
        | 'subtle-primary'
    /**
     * @default 'primary'
     */
    colorScheme?: 'primary' | 'error' | 'success' | 'neutral' | 'warning'
    /**
     * the icon position depends on the document writing direction
     *
     * in **LTR** mode the icon will appear on the left
     *
     * in **RTL** mode the icon will appear on the right
     */
    startIcon?: BaseProps['leftIcon']
    /**
     * the icon position depends on the document writing direction
     *
     * in **LTR** mode the icon will appear on the right
     *
     * in **RTL** mode the icon will appear on the left
     */
    endIcon?: BaseProps['rightIcon']
}

/**
 * Button component
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (props, ref) => {
        const {
            size,
            variant,
            colorScheme,
            startIcon,
            endIcon,
            children,
            sx: externalSx,
            ...rest
        } = props
        const styles = useStyleConfig('Button', {
            size,
            variant,
            colorScheme,
        })
        const sx = merge(styles, externalSx ?? {}, {
            _hover: { ...props._hover },
            _active: { ...props._active },
            _focus: { ...props._focus },
        })

        return (
            <BaseComponent
                data-component="Button"
                ref={ref}
                iconSpacing={1}
                leftIcon={startIcon}
                rightIcon={endIcon}
                sx={sx}
                {...rest}
            >
                {children}
            </BaseComponent>
        )
    },
)
